import { Badge, Button, Card, Checkbox, Col, Empty, message, Popover, Row, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import React from 'react';
import { SUPPLIER_DYNAMICS_TAB_KEYS } from 'components/supplier-dynamics/constants';

interface SupplierProductAlternativesProps {
  product?: any;
  onClickHandler: (alternate: any, tab: string) => void;
  setShowCompareProductsModal: (value: {
    open: boolean;
    comparisonProducts: any[] | undefined;
    unionOfFeatures: string[] | undefined;
  }) => void;
  setCheckedStates: (value: any) => void;
  checkedStates: Record<number, Record<number, boolean>>;
  showMoreDetails?: boolean;
}

export const SupplierProductAlternatives = ({
  product,
  onClickHandler,
  setShowCompareProductsModal,
  setCheckedStates,
  checkedStates,
  showMoreDetails
}: SupplierProductAlternativesProps) => {
  const productId = product?.product?.id;

  const isCompareButtonEnabled = () => {
    return (
      checkedStates && checkedStates[productId] && Object.values(checkedStates[productId])?.some((checked) => checked)
    );
  };

  const handleClearSelection = () => {
    setCheckedStates({});
  };

  const getUnionOfFeatures = (alternates: any[]) => {
    const allFeatures = alternates?.flatMap((alternate) => alternate?.features)?.filter((item) => item);
    const mainProdFeatures = product?.product?.features || [];
    return [...new Set([...mainProdFeatures, ...(allFeatures || [])])];
  };

  const handleProductsComparison = () => {
    const checkedKeys = Object.keys(checkedStates[productId]);
    const mainProd = [product?.product];
    const alternates = product?.alternatives.filter((alternative: any) => checkedKeys?.includes(alternative.id));
    const uniqueFeatures = getUnionOfFeatures(alternates);
    setShowCompareProductsModal({
      open: true,
      comparisonProducts: [...mainProd, ...alternates],
      unionOfFeatures: uniqueFeatures
    });
  };

  const handleCheckboxChange = (alternateId: number, checked: boolean) => {
    if (checked) {
      checkedStates[productId] &&
        Object.keys(checkedStates[productId])?.length > 1 &&
        message.info({
          content: 'You can compare up to three products at a time',
          style: {
            marginTop: '10vh'
          }
        });
      setCheckedStates((prev: any) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [alternateId]: checked
        }
      }));
    } else {
      const newCheckedStates = { ...checkedStates };
      delete newCheckedStates[productId][alternateId];
      Object.keys(newCheckedStates[productId]).length > 0 ? setCheckedStates(newCheckedStates) : setCheckedStates({});
    }
  };

  const disableCheckBoxes = (alternateId: number) => {
    if (
      checkedStates[productId] &&
      Object.keys(checkedStates[productId])?.length >= 3 &&
      !Object.keys(checkedStates[productId]).includes(alternateId.toString())
    ) {
      return true;
    }
    return !!(
      (Object.keys(checkedStates)?.length > 0 ||
        (checkedStates[productId] && Object.keys(checkedStates[productId])?.length >= 3)) &&
      !isCompareButtonEnabled()
    );
  };

  return product?.alternatives.length === 0 ? (
    <Empty description="No alternative products found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
  ) : (
    <Row>
      <Col span={24} className="flex items-center justify-between">
        <span className="text-gray-500 text-xs">TOP ALTERNATIVES ({product?.alternatives.length})</span>
        <div className="flex items-center space-x-2">
          <Button
            key={product.id}
            type="link"
            icon={<ReloadOutlined />}
            disabled={!isCompareButtonEnabled()}
            onClick={() => handleClearSelection()}
            className={`clear-button ${isCompareButtonEnabled() ? 'clear-button-enabled' : ''}`}
          >
            Clear
          </Button>
          <Tooltip title={!isCompareButtonEnabled() ? 'Select at least one item to compare' : ''}>
            <Button
              key={product?.product?.id}
              type="primary"
              disabled={!isCompareButtonEnabled()}
              onClick={() => handleProductsComparison()}
            >
              Compare
            </Button>
          </Tooltip>
        </div>
      </Col>
      <Col span={24}>
        <Row gutter={16} wrap={true}>
          <Col key={product.id} className="grid grid-cols-3 gap-4 mt-2" span={24}>
            {product?.alternatives?.length > 0 &&
              product?.alternatives?.map((alternate: any, index: number) => {
                if (!showMoreDetails && index > 2) return;
                return (
                  <Card
                    key={alternate?.id}
                    bordered={false}
                    style={{
                      borderRadius: '10px',
                      border: '1px solid #d9d9d9'
                    }}
                    actions={[]}
                  >
                    <Row gutter={[0, 4]} wrap={false} className="flex-col">
                      <Col>
                        <Popover
                          title={
                            <div className="flow-root">
                              <h2 className="float-left">{alternate?.name}</h2>
                              <h4 className="float-right text-sm pt-2 pr-2">
                                Matching Features: <Badge count={alternate?.featureMatch} showZero={true} />
                              </h4>
                            </div>
                          }
                          content={
                            <div style={{ width: 600, maxHeight: 150, overflowY: 'auto', padding: 10 }}>
                              {alternate?.description}
                            </div>
                          }
                          trigger="hover"
                          placement="left"
                        >
                          <a
                            title={alternate?.name}
                            className="text-blue-600 text-sm font-medium truncate block"
                            onClick={() => onClickHandler(alternate, 'products')}
                          >
                            {alternate?.name}
                          </a>
                        </Popover>
                      </Col>
                      <Col className="flex gap-1 text-sm">
                        <span>By:</span>
                        <a
                          className="truncate"
                          onClick={() => onClickHandler(alternate, SUPPLIER_DYNAMICS_TAB_KEYS.PURCHASE_CHARACTERISTICS)}
                        >
                          <span title={alternate?.vendor?.name} className="text-blue-600">
                            {alternate?.vendor?.name}
                          </span>
                        </a>
                      </Col>
                      <Col>
                        <Checkbox
                          className="mt-1"
                          key={alternate?.id}
                          checked={(checkedStates[productId] && checkedStates[productId][alternate?.id]) || false}
                          onChange={(e) => handleCheckboxChange(alternate.id, e.target.checked)}
                          disabled={disableCheckBoxes(alternate.id)}
                        >
                          <span className="add-to-compare">Add to compare</span>
                        </Checkbox>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
